import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPendingInvites, respondToInvite } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, 
  MapPin, 
  Calendar, 
  Euro, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  Building2
} from 'lucide-react';
import { cn, formatDate, formatTime } from '../lib/utils';
import { readVenueName } from '../constants/profileContract';
import { DateWithRelative } from '../components/DateWithRelative';
import { toast } from 'sonner';

function CountdownTimer({ remainingSeconds, onExpire }) {
  const [seconds, setSeconds] = useState(remainingSeconds);

  useEffect(() => {
    setSeconds(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (seconds <= 0) {
      onExpire?.();
      return;
    }

    const timer = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [seconds, onExpire]);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const isUrgent = seconds < 3600; // Less than 1 hour

  return (
    <div className={cn(
      "flex items-center gap-1.5 text-sm font-mono",
      isUrgent ? "text-red-400" : "text-yellow-400"
    )}>
      <Clock className="h-4 w-4" />
      {hours > 0 && <span>{hours}h </span>}
      <span>{String(minutes).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span>
    </div>
  );
}

export default function PendingInvitesPage() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [declineModal, setDeclineModal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const navigate = useNavigate();

  const loadInvites = useCallback(async () => {
    try {
      const response = await getPendingInvites();
      setInvites(response.data.invites || []);
    } catch (error) {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
    // Refresh every 30 seconds to catch expiry
    const interval = setInterval(loadInvites, 30000);
    return () => clearInterval(interval);
  }, [loadInvites]);

  const handleConfirm = async (inviteId) => {
    setResponding(inviteId);
    try {
      const response = await respondToInvite(inviteId, 'confirm');
      toast.success('Booking confirmed!');
      const bookingId = response.data?.bookingId;
      if (bookingId) {
        navigate(`/bookings/${bookingId}`);
      } else {
        loadInvites();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setResponding(null);
    }
  };

  const handleDecline = async () => {
    if (!declineModal) return;
    
    setResponding(declineModal);
    try {
      await respondToInvite(declineModal, 'decline', declineReason || null);
      toast.success('Invite declined');
      setDeclineModal(null);
      setDeclineReason('');
      loadInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setResponding(null);
    }
  };

  const handleExpire = (inviteId) => {
    setInvites(prev => prev.filter(i => i.id !== inviteId));
    toast.info('An invite has expired');
  };

  if (loading) {
    return (
      <div className="content-container">
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-40 bg-card animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="content-container">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-white">
          Pending Confirmations
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {invites.length > 0 
            ? `You have ${invites.length} invite${invites.length > 1 ? 's' : ''} awaiting your response`
            : 'No pending invites'
          }
        </p>
      </div>

      {invites.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-foreground mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground text-sm">
              When venues invite you to confirm a gig, it will appear here.
            </p>
            <Link to="/requests">
              <Button className="mt-4" variant="outline">
                Browse Open Requests
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {invites.map((invite) => {
            const snapshot = invite.snapshot || {};
            const isUrgent = invite.remainingSeconds < 3600;
            
            return (
              <Card 
                key={invite.id} 
                className={cn(
                  "bg-card border-2 transition-colors",
                  isUrgent ? "border-red-500/50" : "border-yellow-500/30"
                )}
              >
                <CardContent className="p-4">
                  {/* Header with timer */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Badge className={cn(
                        "text-xs",
                        isUrgent 
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                      )}>
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Action Required
                      </Badge>
                    </div>
                    <CountdownTimer 
                      remainingSeconds={invite.remainingSeconds}
                      onExpire={() => handleExpire(invite.id)}
                    />
                  </div>

                  {/* Venue Info */}
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-foreground">{readVenueName(snapshot)}</span>
                  </div>

                  {/* Request Title */}
                  <h3 className="font-bold text-lg text-foreground mb-3 line-clamp-2">
                    {snapshot.requestTitle || `${snapshot.roleSubtype || snapshot.roleNeeded} Needed`}
                  </h3>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span><DateWithRelative date={snapshot.eventDate} /></span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>{formatTime(snapshot.callTime || snapshot.startTime)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{snapshot.city}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Euro className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-primary">€{snapshot.fee}</span>
                    </div>
                  </div>

                  {/* Notes if any */}
                  {snapshot.notes && (
                    <p className="text-sm text-muted-foreground bg-secondary/30 p-2 rounded mb-4">
                      {snapshot.notes}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleConfirm(invite.id)}
                      disabled={responding === invite.id}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`confirm-invite-${invite.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {responding === invite.id ? 'Confirming...' : 'Confirm'}
                    </Button>
                    <Button
                      onClick={() => setDeclineModal(invite.id)}
                      disabled={responding === invite.id}
                      variant="outline"
                      className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
                      data-testid={`decline-invite-${invite.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Decline Modal */}
      <Dialog open={!!declineModal} onOpenChange={() => setDeclineModal(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Decline Invite</DialogTitle>
            <DialogDescription>
              Let the venue know why you can't take this gig (optional).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder="e.g., Already booked, travel distance, etc."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="bg-secondary/50 border-border"
              rows={3}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeclineModal(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDecline}
                disabled={responding}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {responding ? 'Declining...' : 'Decline Invite'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
